import { useEffect, useMemo, useState } from 'react'

// 数字 pid → Agent ID 映射表地址（同源路由，经 Cloudflare Worker 提供，国内访问稳定）
const PID_MAP_URL = '/api/pid_map.json'

// 只允许 1-32 位数字或英文字母 ID
function isValidId(v: unknown): v is string {
  return typeof v === 'string' && /^[A-Za-z0-9]{1,32}$/.test(v)
}

// 同步获取 URL 中的原始 pid（数字或英文ID）
export function getRawPid(): string | null {
  try {
    const raw = (new URLSearchParams(window.location.search).get('pid') || '').trim()
    return isValidId(raw) ? raw : null
  } catch {
    return null
  }
}

// 映射表模块级缓存，多个组件共享一次请求
let mapPromise: Promise<Record<string, string>> | null = null
function loadPidMap(): Promise<Record<string, string>> {
  if (!mapPromise) {
    mapPromise = fetch(PID_MAP_URL, { cache: 'no-store' })
      .then(res => (res.ok ? res.json() : {}))
      .then((data: Record<string, unknown>) => {
        const map: Record<string, string> = {}
        Object.keys(data).forEach(k => {
          const v = data[k]
          if (isValidId(v)) map[k] = v
        })
        return map
      })
      .catch(() => ({}))
  }
  return mapPromise
}

export type AgentIdInfo = {
  raw: string | null       // URL 中的原始 pid
  resolved: string | null  // 最终展示 ID；数字 pid 无映射时为 null（绝不显示数字）
  ready: boolean           // 映射解析是否完成
}

// Hook：英文 pid 直接作为 ID；数字 pid 经映射表解析
export function useAgentId(): AgentIdInfo {
  const raw = useMemo(getRawPid, [])
  const isNumeric = !!raw && /^\d+$/.test(raw)
  const [resolved, setResolved] = useState<string | null>(isNumeric ? null : raw)
  const [ready, setReady] = useState(!isNumeric)

  useEffect(() => {
    if (!raw || !/^\d+$/.test(raw)) return
    let alive = true
    loadPidMap().then(map => {
      if (!alive) return
      const v = map[raw]
      setResolved(isValidId(v) ? v : null)
      setReady(true)
    })
    return () => { alive = false }
  }, [raw])

  return { raw, resolved, ready }
}
